<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { UiAlert, UiButton, UiField, UiInput, useOverlay } from '@dashboard/ui-kit';
import { onStepUpRequired } from '../api';
import type { RefusalReason } from '../api';
import { explainStepUp, startStepUp, submitStepUp } from '../step-up';
import type { StepUpMethod } from '../step-up';

/**
 * The whole of what `016` replaced the old re-authentication flow with.
 *
 * `012` sent people to their inbox mid-task: a magic link is a navigation, so
 * whatever they were in the middle of was gone by the time they came back.
 * This asks for a code in place and the request that was refused is retried —
 * `request` does the retrying, so every module's writes get it without
 * knowing this exists.
 *
 * Mounted once by the shell. It registers itself with the api client and then
 * waits.
 */

const open = ref(false);
/** `mfa-required` has nothing to type — it is an explanation and a way out. */
const reason = ref<RefusalReason>('step-up');
const method = ref<StepUpMethod>('email');
const sent = ref(false);
const code = ref('');
const busy = ref(false);
const problem = ref('');
const field = ref<{ focus: () => void }>();
const panel = ref<HTMLElement>();

/**
 * The focus trap, scroll lock and Escape handling every overlay owes.
 *
 * This was hand-rolled at first, with a `@keydown.esc` and nothing else — so
 * Tab walked onto the page behind it and the page scrolled under it. The kit
 * says exactly why that is not acceptable and exports this for overlays it
 * does not itself cover, which this is: `UiModal` is the confirm surface and
 * takes no form, and this is neither that nor an edit surface.
 *
 * Not closed by its backdrop. A stray click should not throw away a code
 * somebody has half typed.
 */
useOverlay({
	open: () => open.value,
	container: panel,
	onClose: () => {
		close(false);
	},
	closeOnBackdrop: false,
});

/** Resolved when the user either succeeds or gives up. */
let settle: ((retry: boolean) => void) | undefined;

/**
 * One prompt at a time.
 *
 * A page that fires two writes at once gets two refusals, and without this the
 * second would overwrite `settle` and leave the first request waiting on a
 * promise nothing will ever resolve. Both wait on the same answer instead.
 */
let pending: Promise<boolean> | undefined;

onStepUpRequired((why) => {
	reason.value = why;
	// Set **synchronously**, before any await. An earlier version assigned it
	// after awaiting the start call, so two writes refused together both got
	// past the guard and the second overwrote the first's resolver — leaving
	// that request waiting on a promise nothing would ever settle.
	if (pending !== undefined) return pending;
	pending = new Promise<boolean>((resolve) => {
		settle = resolve;
	});

	if (why === 'step-up') void begin();
	else open.value = true;

	return pending;
});

async function begin(): Promise<void> {
	open.value = true;
	code.value = '';
	problem.value = '';
	busy.value = true;

	try {
		const started = await startStepUp();
		method.value = started.method;
		sent.value = started.method === 'email' && started.sent;
		if (started.method === 'email' && !started.sent) {
			problem.value =
				'Too many codes have been requested for this address. Wait a few minutes and try again.';
		}
	} catch {
		problem.value = 'That did not work. Try again.';
	} finally {
		busy.value = false;
	}

	await nextTick();
	field.value?.focus();
}

function close(retry: boolean): void {
	open.value = false;
	code.value = '';
	settle?.(retry);
	settle = undefined;
	pending = undefined;
}

async function submit(): Promise<void> {
	busy.value = true;
	problem.value = '';
	try {
		await submitStepUp(code.value);
		close(true);
	} catch (error) {
		problem.value = explainStepUp(error);
		code.value = '';
		await nextTick();
		field.value?.focus();
	} finally {
		busy.value = false;
	}
}
</script>

<template>
	<!--
		Teleported, because a write can be refused from inside a drawer and a
		dialog nested in one inherits its stacking context and its overflow.

		**Above every surface a write can be launched from**, which is why the
		z-index is its own rather than the kit's `z-50`. This dialog is mounted
		by the app shell, so its teleport anchor is the earliest in `<body>`
		— and at an equal z-index the later node paints on top. Deleting a site
		asks from a `UiModal`, also `z-50`, and the dialog came up *behind* it:
		a refusal with no visible way to clear it, and a console showing a 401
		nobody answered.
	-->
	<Teleport to="body">
		<div
			v-if="open"
			class="fixed inset-0 z-60 flex items-center justify-center bg-black/40 p-4"
		>
			<div
				ref="panel"
				role="dialog"
				aria-modal="true"
				aria-labelledby="step-up-title"
				tabindex="-1"
				class="bg-surface w-full max-w-sm rounded-lg p-5 shadow-lg focus:outline-none"
			>
				<!--
					A dead end, but an explained one with a way out of it. No
					field, because no code clears this — the deployment wants
					an authenticator and this person has none.
				-->
				<template v-if="reason === 'mfa-required'">
					<h2 id="step-up-title" class="text-base font-semibold">
						An authenticator app is needed
					</h2>
					<p class="text-muted mt-1 text-sm">
						This dashboard is set up so that changes are confirmed with a code from an
						authenticator app. Set one up on your profile, then make the change again.
					</p>
					<div class="mt-4 flex justify-end gap-2">
						<UiButton type="button" @click="close(false)">Not now</UiButton>
						<!--
							By route **name**, not by path. The first version
							hardcoded `/account`, which is not a route this
							app has — the link went nowhere, and nothing could
							have caught it because a string is a string. A
							name that does not exist fails loudly instead.
						-->
						<RouterLink
							class="bg-accent inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium text-white"
							:to="{ name: 'profile' }"
							@click="close(false)"
						>
							Go to my profile
						</RouterLink>
					</div>
				</template>

				<template v-else>
					<h2 id="step-up-title" class="text-base font-semibold">Confirm it is you</h2>

					<p class="text-muted mt-1 text-sm">
						<template v-if="method === 'authenticator'">
							Enter the current code from your authenticator app.
						</template>
						<template v-else-if="sent">
							We have emailed you a six-digit code. It works once and expires in ten
							minutes.
						</template>
						<template v-else> Something went wrong starting this. </template>
					</p>

					<UiAlert v-if="problem" class="mt-3">{{ problem }}</UiAlert>

					<form class="mt-4 flex flex-col gap-4" @submit.prevent="submit">
						<UiField label="Code">
							<!--
								`inputmode` and `autocomplete` so a phone shows
								a number pad and offers the code straight from
								the message, which is most of the ergonomic win.
							-->
							<UiInput
								ref="field"
								v-model="code"
								inputmode="numeric"
								autocomplete="one-time-code"
								maxlength="6"
								placeholder="000000"
							/>
						</UiField>

						<div class="flex justify-end gap-2">
							<UiButton type="button" @click="close(false)">Cancel</UiButton>
							<UiButton
								type="submit"
								variant="primary"
								:busy="busy"
								:disabled="code.trim().length < 6"
							>
								Confirm
							</UiButton>
						</div>
					</form>
				</template>
			</div>
		</div>
	</Teleport>
</template>
